import { useEffect, useRef } from 'react';

/**
 * 손가락·펜 서명 — 투명 PNG data URL. safe 의 서명 판(`SafeSignPage`)과 같은 그리기다.
 *
 * <p>그린 뒤 손을 떼는 순간 {@code onChange} 로 PNG 가 나간다. 지우면 null.
 */
export function SignaturePad({ value, onChange }: { value: string | null; onChange: (value: string | null) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const drawn = useRef(false);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const ratio = window.devicePixelRatio || 1;
    el.width = el.clientWidth * ratio;
    el.height = el.clientHeight * ratio;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111';
  }, []);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top] as const;
  };

  const clear = () => {
    const el = canvas.current;
    if (!el) return;
    el.getContext('2d')?.clearRect(0, 0, el.width, el.height);
    drawn.current = false;
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvas}
        aria-label="서명 칸"
        style={{
          width: '100%',
          height: '160px',
          border: '1px dashed var(--border-strong, #94a3b8)',
          borderRadius: '8px',
          background: '#fff',
          touchAction: 'none',
          display: 'block',
        }}
        onPointerDown={(e) => {
          drawing.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          const [x, y] = point(e);
          const ctx = e.currentTarget.getContext('2d');
          ctx?.beginPath();
          ctx?.moveTo(x, y);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const [x, y] = point(e);
          const ctx = e.currentTarget.getContext('2d');
          ctx?.lineTo(x, y);
          ctx?.stroke();
          drawn.current = true;
        }}
        onPointerUp={(e) => {
          drawing.current = false;
          if (drawn.current) onChange(e.currentTarget.toDataURL('image/png'));
        }}
      />
      <div className="mt-1 flex justify-end">
        <button type="button" className="btn-secondary" onClick={clear} disabled={!value}>
          지우기
        </button>
      </div>
    </div>
  );
}
